import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PageHeader from "../layout/PageHeader";
import {
  deleteAgent247Event,
  fetchAgent247Events,
  fetchAgent247EventDetail,
  patchAgent247EventFollowUpStatus,
} from "../../utils/api";
import {
  formatAiAssistantSeenBy,
  formatAiAssistantSeenByTitle,
  getStableEventId,
  markAiAssistantEventSeen,
} from "../../utils/aiAssistantReadState";
import { resolveCallerFirstName } from "../../utils/agent247CallerDisplay";
import { resolveAgent247OrganizationId } from "../../utils/resolveAgent247OrganizationId";
import { getTranscriptBubbleStyle } from "../../utils/transcriptMessageStyles";
import {
  assessAgent247TranscriptHealth,
  resolveAgent247TurnMessage,
} from "../../utils/agent247TranscriptHealth";
import { API_URL } from "../../apiConfig";
import Agent247NotificationRecipients from "./Agent247NotificationRecipients";

const DEV_FORCED_AI_ASSISTANT_ORG_ID = "695efe91312e4c3878fcf24a";
const isDevEnv = process.env.NODE_ENV !== "production";

const sortEventsDesc = (rows) =>
  [...(Array.isArray(rows) ? rows : [])].sort((a, b) => {
    const at = new Date(a?.timestamp || a?.createdAt || 0).getTime();
    const bt = new Date(b?.timestamp || b?.createdAt || 0).getTime();
    return bt - at;
  });

const formatDuration = (secondsRaw) => {
  const total = Number(secondsRaw || 0);
  if (!Number.isFinite(total) || total <= 0) return "--";
  const m = Math.floor(total / 60);
  const s = Math.floor(total % 60);
  return `${m}m ${s}s`;
};

const mapSpeaker = (raw) => {
  const value = String(raw || "").toLowerCase();
  if (value.includes("agent") || value.includes("assistant") || value.includes("kathy")) {
    return "Kathy";
  }
  return "Caller";
};

const reasonBadgeStyle = (reasonRaw) => {
  const reason = String(reasonRaw || "").toLowerCase();
  if (reason.includes("complaint")) {
    return {
      background: "#fee2e2",
      color: "#b91c1c",
      border: "1px solid #fecaca",
    };
  }
  if (reason.includes("pricing") || reason.includes("cost")) {
    return {
      background: "#fff7ed",
      color: "#c2410c",
      border: "1px solid #fed7aa",
    };
  }
  return {
    background: "#eff6ff",
    color: "#1d4ed8",
    border: "1px solid #bfdbfe",
  };
};

const urgencyBadgeStyle = (urgencyRaw) => {
  const urgency = String(urgencyRaw || "").toLowerCase();
  if (urgency === "critical" || urgency === "high") {
    return {
      background: "#fee2e2",
      color: "#b91c1c",
      border: "1px solid #fecaca",
    };
  }
  return {
    background: "#ecfeff",
    color: "#0f766e",
    border: "1px solid #a5f3fc",
  };
};

const FOLLOW_UP_STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "following_up", label: "Following up" },
  { value: "callback_scheduled", label: "Callback scheduled" },
  { value: "resolved", label: "Resolved" },
  { value: "escalated", label: "Escalated" },
];

const FOLLOW_UP_STATUS_SET = new Set(FOLLOW_UP_STATUS_OPTIONS.map((o) => o.value));

const normalizeFollowUpStatus = (raw) => {
  const v = String(raw || "").trim();
  return FOLLOW_UP_STATUS_SET.has(v) ? v : "open";
};

const Agent247Settings = (props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [eventsRefreshing, setEventsRefreshing] = useState(false);
  const hasLoadedEventsRef = useRef(false);
  const [events, setEvents] = useState([]);
  const [infoMessage, setInfoMessage] = useState("");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [urgencyFilter, setUrgencyFilter] = useState("all");
  const [deletingEventId, setDeletingEventId] = useState("");
  const [statusSavingId, setStatusSavingId] = useState("");
  const handledEventFromUrlRef = useRef("");

  const organizationId = useMemo(() => {
    const resolved = resolveAgent247OrganizationId(
      props.activeLocation,
      props.currentUser,
    );
    if (resolved) return resolved;
    if (isDevEnv) return DEV_FORCED_AI_ASSISTANT_ORG_ID;
    return "";
  }, [props.activeLocation, props.currentUser]);
  const eventFromQuery = useMemo(
    () => String(searchParams.get("event") || "").trim(),
    [searchParams],
  );

  const applySeenToEventInList = (event) => {
    if (!organizationId || !event) return event;
    const updated = markAiAssistantEventSeen(organizationId, event);
    const eventId = getStableEventId(event);
    setEvents((prev) =>
      prev.map((e) => (getStableEventId(e) === eventId ? updated : e)),
    );
    if (selectedEvent && getStableEventId(selectedEvent) === eventId) {
      setSelectedEvent(updated);
    }
    return updated;
  };

  // Perf-audit fix (2026-07-24): the list response no longer includes the full
  // transcript/raw payload (see routes/agent247.js EVENTS_LIST_SELECT), so opening the
  // detail panel now lazy-fetches the complete event on demand instead of relying on
  // data that used to already be sitting in the list row. Shows the list-row version
  // immediately (fast, has caller/phone/reason/outcome already) then swaps in the full
  // payload once it arrives.
  const openEventDetail = async (event) => {
    const listVersion = applySeenToEventInList(event);
    setSelectedEvent(listVersion);
    const eventId = String(event?._id || "").trim();
    if (!organizationId || !eventId) return;
    try {
      const detail = await fetchAgent247EventDetail(organizationId, eventId);
      setSelectedEvent((prev) =>
        prev && getStableEventId(prev) === getStableEventId(listVersion)
          ? { ...listVersion, ...detail }
          : prev,
      );
    } catch {
      // Detail panel just keeps showing the list-row fields (caller/phone/reason/outcome
      // already present) — transcript/deep fields silently stay unavailable rather than
      // blocking the panel from opening at all.
    }
  };

  useEffect(() => {
    if (!isDevEnv) return;
    console.log("[WEB AI_ASSISTANT] organizationId", organizationId || "(empty)");
  }, [organizationId]);

  // handledEventFromUrlRef guards against re-firing on unstable `events`/`setSearchParams`
  // deps below — do not remove without replacing the guard.
  // (Previously caused a replaceState() infinite loop pattern — see 2026-07-02 fix.)
  useEffect(() => {
    if (!eventFromQuery) {
      handledEventFromUrlRef.current = "";
      return;
    }
    if (handledEventFromUrlRef.current === eventFromQuery) return;
    if (!organizationId || events.length === 0) return;
    const match = events.find((e) => getStableEventId(e) === eventFromQuery);
    if (!match) return;
    handledEventFromUrlRef.current = eventFromQuery;
    void openEventDetail(match);
    setSearchParams(
      (prev) => {
        if (!prev.get("event")) return prev;
        const next = new URLSearchParams(prev);
        next.delete("event");
        return next;
      },
      { replace: true },
    );
  }, [eventFromQuery, events, organizationId, setSearchParams]);

  // Perf-audit fix (2026-07-24): search used to be client-side (filter events already
  // fetched, matching against JSON.stringify(event.payload) for every row). The list
  // endpoint no longer ships the full payload needed for that, so search now runs
  // server-side (routes/agent247.js SEARCHABLE_EVENT_FIELDS) — debounced so typing
  // doesn't fire a request per keystroke.
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    hasLoadedEventsRef.current = false;
  }, [organizationId]);

  useEffect(() => {
    if (!organizationId) {
      setLoading(false);
      return;
    }
    let active = true;
    const loadEvents = async ({ background = false } = {}) => {
      if (!active) return;
      const silent = background || hasLoadedEventsRef.current;
      if (silent) {
        setEventsRefreshing(true);
      } else {
        setLoading(true);
      }
      if (!silent) {
        setInfoMessage("");
      }
      try {
        const rows = await fetchAgent247Events(organizationId, 150, debouncedSearchQuery);
        if (!active) return;
        setEvents(sortEventsDesc(Array.isArray(rows) ? rows : []));
        hasLoadedEventsRef.current = true;
      } catch {
        if (!active) return;
        if (!silent) {
          setEvents([]);
          setInfoMessage(
            "24/7 Inquiry Call Assist feedback data is not available yet for this agency.",
          );
        }
      } finally {
        if (active) {
          setLoading(false);
          setEventsRefreshing(false);
        }
      }
    };
    loadEvents({ background: false });
    const interval = setInterval(() => loadEvents({ background: true }), 20000);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [organizationId, debouncedSearchQuery]);

  useEffect(() => {
    if (!organizationId) return undefined;
    const token = localStorage.getItem("token");
    if (!token) return undefined;

    const controller = new AbortController();
    const connect = async () => {
      try {
        const response = await fetch(
          `${API_URL}/agent247/events/stream?organizationId=${encodeURIComponent(organizationId)}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "text/event-stream",
            },
            signal: controller.signal,
          },
        );
        if (!response.ok || !response.body) return;

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        while (!controller.signal.aborted) {
          const next = await reader.read();
          if (next.done) break;
          buffer += decoder.decode(next.value, { stream: true });
          const chunks = buffer.split("\n\n");
          buffer = chunks.pop() || "";
          for (const chunk of chunks) {
            const eventLine = chunk
              .split("\n")
              .find((line) => line.startsWith("event:"));
            const dataLine = chunk
              .split("\n")
              .find((line) => line.startsWith("data:"));
            if (!eventLine || !dataLine) continue;
            const eventName = eventLine.replace("event:", "").trim();
            if (eventName !== "agent247_event") continue;
            try {
              const payload = JSON.parse(dataLine.replace("data:", "").trim());
              setEvents((prev) => {
                const key = String(payload?._id || `${payload?.callId}-${payload?.eventType}`);
                const existing = prev.find(
                  (row) => String(row?._id || `${row?.callId}-${row?.eventType}`) === key,
                );
                const merged = {
                  ...(existing || {}),
                  ...payload,
                  followUpStatus:
                    payload.followUpStatus !== undefined && payload.followUpStatus !== null
                      ? payload.followUpStatus
                      : existing?.followUpStatus,
                };
                const deduped = prev.filter(
                  (row) => String(row?._id || `${row?.callId}-${row?.eventType}`) !== key,
                );
                return sortEventsDesc([merged, ...deduped]);
              });
            } catch {
              // ignore malformed SSE data chunks
            }
          }
        }
      } catch {
        // keep polling fallback
      }
    };

    void connect();
    return () => controller.abort();
  }, [organizationId]);

  const stats = useMemo(() => {
    const total = events.length;
    const highUrgency = events.filter((event) => {
      const urgency = String(event?.urgency || "").toLowerCase();
      return urgency === "high" || urgency === "critical";
    }).length;
    const uniqueCalls = new Set(
      events.map((event) => String(event?.callId || "").trim()).filter(Boolean),
    ).size;
    return { total, highUrgency, uniqueCalls };
  }, [events]);

  const typeOptions = useMemo(
    () =>
      Array.from(
        new Set(events.map((event) => String(event?.eventType || "").trim()).values()),
      ).filter(Boolean),
    [events],
  );

  const urgencyOptions = useMemo(
    () =>
      Array.from(
        new Set(events.map((event) => String(event?.urgency || "").trim()).values()),
      ).filter(Boolean),
    [events],
  );

  // Text search now happens server-side (see debouncedSearchQuery above) — `events`
  // already only contains matches. Type/urgency stay client-side since those options
  // are derived from the same already-fetched small list and don't need another round trip.
  const filteredEvents = useMemo(() => {
    return events.filter((event) => {
      const typeValue = String(event?.eventType || "").trim();
      const urgencyValue = String(event?.urgency || "").trim();
      if (typeFilter !== "all" && typeValue !== typeFilter) return false;
      if (urgencyFilter !== "all" && urgencyValue !== urgencyFilter) return false;
      return true;
    });
  }, [events, typeFilter, urgencyFilter]);

  const formatDateTime = (value) => {
    if (!value) return "--";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "--";
    return date.toLocaleString();
  };

  const readPath = (obj, path) => {
    const keys = String(path || "")
      .split(".")
      .filter(Boolean);
    let cur = obj;
    for (const key of keys) {
      if (cur && typeof cur === "object" && key in cur) {
        cur = cur[key];
      } else {
        return "";
      }
    }
    return cur ?? "";
  };

  const firstNonEmpty = (obj, paths) => {
    for (const path of paths) {
      const value = readPath(obj, path);
      if (value !== undefined && value !== null && String(value).trim() !== "") {
        return String(value).trim();
      }
    }
    return "";
  };

  const eventDetails = useMemo(() => {
    if (!selectedEvent) return null;
    const payload = selectedEvent?.payload || {};
    const email = firstNonEmpty(payload, [
      "email",
      "contact.email",
      "caller.email",
      "analysis.contact.email",
      "conversation_initiation_client_data.dynamic_variables.email",
      "conversation.conversation_initiation_client_data.dynamic_variables.email",
      "metadata.email",
    ]);
    const phone = firstNonEmpty(payload, [
      "callerPhone",
      "normalized.callerPhone",
      "data.user_id",
      "data.metadata.phone_call.external_number",
      "phone",
      "contact.phone",
      "caller.phone",
      "analysis.contact.phone",
      "called_number",
      "from_number",
      "to_number",
      "conversation_initiation_client_data.dynamic_variables.phone",
      "conversation.conversation_initiation_client_data.dynamic_variables.phone",
    ]);
    const need = firstNonEmpty(payload, [
      "summary",
      "normalized.summary",
      "data.analysis.transcript_summary",
      "data.analysis.call_summary_title",
    ]);
    const reason = firstNonEmpty(payload, [
      "reason",
      "normalized.reason",
      "intent",
      "data.analysis.call_summary_title",
    ]);
    const outcome = firstNonEmpty(payload, ["outcome", "normalized.outcome"]);
    const language = firstNonEmpty(payload, [
      "language",
      "normalized.language",
      "data.metadata.main_language",
      "data.metadata.language",
    ]);
    const nextStep = firstNonEmpty(payload, [
      "recommendedNextStep",
      "normalized.recommendedNextStep",
    ]);
    const durationSecs = firstNonEmpty(payload, [
      "duration",
      "normalized.duration",
      "data.metadata.call_duration_secs",
      "data.metadata.system__call_duration_secs",
    ]);
    const status = firstNonEmpty(payload, ["status", "normalized.status", "data.status"]);
    const agentName = firstNonEmpty(payload, [
      "agentName",
      "agent_name",
      "normalized.agentName",
      "data.agent_name",
    ]);
    const transcriptRows = Array.isArray(payload?.transcriptTurns)
      ? payload.transcriptTurns
      : Array.isArray(payload?.transcript)
        ? payload.transcript
        : Array.isArray(payload?.data?.transcript)
          ? payload.data.transcript
        : [];
    const transcriptText = firstNonEmpty(payload, ["transcript", "normalized.transcript"]);

    return {
      payload,
      email,
      callerPhone: phone,
      callerName: resolveCallerFirstName(payload),
      summary: need,
      reason: reason || "General Question",
      outcome: outcome || "Information Provided",
      language: language || "--",
      nextStep: nextStep || "No action needed",
      durationSecs,
      status: status || "completed",
      agentName: agentName || "Kathy - 247",
      transcriptRows: transcriptRows.map((row) => ({
        role: mapSpeaker(row?.speaker || row?.role),
        message: resolveAgent247TurnMessage(row),
      })),
      transcriptText,
    };
  }, [selectedEvent]);

  const transcriptHealth = useMemo(() => {
    if (!eventDetails) return null;
    return assessAgent247TranscriptHealth(
      eventDetails.transcriptRows,
      selectedEvent?.payload || {},
    );
  }, [eventDetails, selectedEvent]);

  const handleFollowUpStatusChange = async (row, followUpStatus) => {
    const eventId = String(row?._id || "").trim();
    if (!eventId || !organizationId) return;
    setStatusSavingId(eventId);
    setInfoMessage("");
    try {
      const updated = await patchAgent247EventFollowUpStatus(
        organizationId,
        eventId,
        followUpStatus,
      );
      setEvents((prev) =>
        sortEventsDesc(
          prev.map((ev) => (String(ev?._id) === eventId ? { ...ev, ...updated } : ev)),
        ),
      );
      setSelectedEvent((prev) =>
        prev && String(prev._id) === eventId ? { ...prev, ...updated } : prev,
      );
    } catch {
      setInfoMessage("Failed to save status. Please try again.");
    } finally {
      setStatusSavingId("");
    }
  };

  const handleDeleteEvent = async (event) => {
    if (!organizationId) return;
    const eventId = String(event?._id || "").trim();
    if (!eventId) return;
    const callId = String(event?.callId || "").trim();
    const confirmed = window.confirm(
      `Delete this 24/7 Inquiry Call Assist record permanently from database?${callId ? `\n\nCall ID: ${callId}` : ""}`,
    );
    if (!confirmed) return;

    try {
      setDeletingEventId(eventId);
      await deleteAgent247Event(organizationId, eventId);
      setEvents((prev) => prev.filter((row) => String(row?._id || "") !== eventId));
      setSelectedEvent((prev) =>
        String(prev?._id || "") === eventId ? null : prev,
      );
      setInfoMessage("24/7 Inquiry Call Assist event deleted.");
    } catch {
      setInfoMessage("Failed to delete 24/7 Inquiry Call Assist event. Please try again.");
    } finally {
      setDeletingEventId("");
    }
  };

  return (
    <div className="page-shell ai-hiring-page">
      <PageHeader
        title="24/7 Inquiry Call Assist"
        subtitle="Live call feedback and activity for your agency."
        icon={
          <span role="img" aria-label="24/7 Inquiry Call Assist">
            📞
          </span>
        }
        {...props}
      />
      <div className="ai-hiring-content">
        <Agent247NotificationRecipients organizationId={organizationId} />

        {!organizationId ? (
          <div className="ai-empty">
            Please select a location from the top menu first.
          </div>
        ) : loading ? (
          <div className="ai-empty">Loading 24/7 Inquiry Call Assist data...</div>
        ) : (
          <>
            {infoMessage ? (
              <div className="ai-alert ai-alert-error">
                {infoMessage}
                {" If this problem continues, contact your administrator."}
              </div>
            ) : null}

            <div className="ai-settings-grid">
              <div className="ai-card ai-settings-card">
                <p className="ai-card-note">Total Events</p>
                <h3 className="ai-card-title">{stats.total}</h3>
              </div>
              <div className="ai-card ai-settings-card">
                <p className="ai-card-note">Unique Calls</p>
                <h3 className="ai-card-title">{stats.uniqueCalls}</h3>
              </div>
              <div className="ai-card ai-settings-card">
                <p className="ai-card-note">High/Critical Alerts</p>
                <h3 className="ai-card-title">{stats.highUrgency}</h3>
              </div>
            </div>

            <div className="ai-card">
              <div className="ai-card-header">
                <div>
                  <p className="ai-card-note">24/7 Inquiry Call Assist Feedback</p>
                  <h3 className="ai-card-title">
                    Recent Conversation Events
                    {eventsRefreshing ? (
                      <span
                        style={{
                          marginLeft: 10,
                          fontSize: 12,
                          fontWeight: 500,
                          color: "#64748b",
                        }}
                      >
                        Updating…
                      </span>
                    ) : null}
                  </h3>
                </div>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1fr 1fr",
                  gap: 10,
                  marginBottom: 10,
                }}
              >
                <input
                  className="ai-input"
                  placeholder="Search phone, email, need, intent, text..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <select
                  className="ai-input"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <option value="all">All Types</option>
                  {typeOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <select
                  className="ai-input"
                  value={urgencyFilter}
                  onChange={(e) => setUrgencyFilter(e.target.value)}
                >
                  <option value="all">All Urgency</option>
                  {urgencyOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
              <div className="ai-table-wrapper">
                <table className="ai-table ai-table-conversation-events" style={{ minWidth: 1320 }}>
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Caller</th>
                      <th>Phone</th>
                      <th>Reason</th>
                      <th>Outcome</th>
                      <th>Urgency</th>
                      <th>Status</th>
                      <th>Seen by</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEvents.length === 0 ? (
                      <tr>
                        <td colSpan="9">No matching 24/7 Inquiry Call Assist events.</td>
                      </tr>
                    ) : (
                      filteredEvents.map((event) => (
                        <tr
                          key={event?._id || `${event?.callId}-${event?.timestamp}`}
                          style={{
                            cursor: "pointer",
                            borderBottom: "1px solid #e2e8f0",
                            background: "#ffffff",
                          }}
                          onClick={() => {
                            void openEventDetail(event);
                          }}
                        >
                          <td>{formatDateTime(event?.timestamp || event?.createdAt)}</td>
                          <td>
                            {resolveCallerFirstName(event?.payload || {})}
                          </td>
                          <td>
                            {event?.payload?.callerPhone ||
                              event?.payload?.normalized?.callerPhone ||
                              event?.payload?.data?.user_id ||
                              event?.payload?.data?.metadata?.phone_call?.external_number ||
                              "--"}
                          </td>
                          <td>
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                borderRadius: 999,
                                padding: "4px 10px",
                                fontSize: 12,
                                fontWeight: 700,
                                ...(reasonBadgeStyle(
                                  event?.payload?.reason ||
                                    event?.payload?.normalized?.reason ||
                                    event?.payload?.data?.analysis?.call_summary_title ||
                                    event?.category ||
                                    "General Question",
                                ) || {}),
                              }}
                            >
                              {event?.payload?.reason ||
                                event?.payload?.normalized?.reason ||
                                event?.payload?.data?.analysis?.call_summary_title ||
                                event?.category ||
                                "General Question"}
                            </span>
                          </td>
                          <td>
                            {event?.payload?.outcome ||
                              event?.payload?.normalized?.outcome ||
                              "Information Provided"}
                          </td>
                          <td>
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                borderRadius: 999,
                                padding: "4px 10px",
                                fontSize: 12,
                                fontWeight: 700,
                                textTransform: "capitalize",
                                ...(urgencyBadgeStyle(event?.urgency || "") || {}),
                              }}
                            >
                              {event?.urgency || "--"}
                            </span>
                          </td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <select
                              className="ai-conversation-status-select"
                              value={normalizeFollowUpStatus(event?.followUpStatus)}
                              onChange={(e) =>
                                handleFollowUpStatusChange(event, e.target.value)
                              }
                              disabled={statusSavingId === String(event?._id || "")}
                              aria-label="Follow-up status"
                            >
                              {FOLLOW_UP_STATUS_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td
                            title={formatAiAssistantSeenByTitle(event?.seenBy)}
                            style={{ maxWidth: 200 }}
                          >
                            {formatAiAssistantSeenBy(event?.seenBy)}
                          </td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              className="ai-button ai-button-secondary"
                              onClick={() => handleDeleteEvent(event)}
                              disabled={deletingEventId === String(event?._id || "")}
                              style={{
                                minWidth: 78,
                                borderColor: "#fecaca",
                                color: "#b91c1c",
                              }}
                            >
                              {deletingEventId === String(event?._id || "")
                                ? "Deleting..."
                                : "Delete"}
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
      {selectedEvent && eventDetails ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.45)",
            zIndex: 1200,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: 16,
          }}
          onClick={() => setSelectedEvent(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(1040px, 96vw)",
              maxHeight: "88vh",
              overflowY: "auto",
              background: "#fff",
              borderRadius: 18,
              border: "1px solid #dbeafe",
              boxShadow: "0 20px 40px rgba(15, 23, 42, 0.18)",
              padding: 18,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 20, color: "#0f172a" }}>Conversation Details</h3>
                <div style={{ color: "#64748b", marginTop: 2 }}>
                  {`Conversation with ${eventDetails.agentName || "Kathy - 247"} • ${formatDateTime(selectedEvent?.timestamp || selectedEvent?.createdAt)} • Duration: ${formatDuration(eventDetails.durationSecs)} • Call Status: ${String(eventDetails.status || "completed").replace(/_/g, " ")}`}
                </div>
                <div style={{ color: "#64748b", marginTop: 4, fontSize: 13 }}>
                  <strong>Seen by:</strong>{" "}
                  {formatAiAssistantSeenBy(selectedEvent?.seenBy)}
                </div>
              </div>
              <button
                type="button"
                className="ai-button ai-button-secondary"
                onClick={() => setSelectedEvent(null)}
              >
                Close
              </button>
            </div>

            <div style={{ marginBottom: 14, maxWidth: 340 }}>
              <p className="ai-card-note" style={{ margin: "0 0 6px" }}>
                Status
              </p>
              <select
                className="ai-conversation-status-select ai-conversation-status-select--modal"
                value={normalizeFollowUpStatus(selectedEvent?.followUpStatus)}
                onChange={(e) =>
                  handleFollowUpStatusChange(selectedEvent, e.target.value)
                }
                disabled={statusSavingId === String(selectedEvent?._id || "")}
                aria-label="Follow-up status"
              >
                {FOLLOW_UP_STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 12,
              }}
            >
              <div className="ai-card ai-settings-card" style={{ background: "#f8fbff", borderRadius: 12 }}>
                <p className="ai-card-note">Caller Name</p>
                <h3 className="ai-card-title">{eventDetails.callerName || "Caller"}</h3>
              </div>
              <div className="ai-card ai-settings-card" style={{ background: "#f8fbff", borderRadius: 12 }}>
                <p className="ai-card-note">Reason for Call</p>
                <h3 className="ai-card-title">{eventDetails.reason || "General Question"}</h3>
              </div>
              <div className="ai-card ai-settings-card" style={{ background: "#f8fbff", borderRadius: 12 }}>
                <p className="ai-card-note">Outcome</p>
                <h3 className="ai-card-title">{eventDetails.outcome || "Information Provided"}</h3>
              </div>
              <div className="ai-card ai-settings-card" style={{ background: "#f8fbff", borderRadius: 12 }}>
                <p className="ai-card-note">Contact</p>
                <h3 className="ai-card-title">{eventDetails.callerPhone || "--"}</h3>
                <div style={{ color: "#64748b", marginTop: 4, fontSize: 13 }}>
                  {`Language: ${eventDetails.language || "--"}`}
                </div>
              </div>
            </div>

            <div className="ai-card" style={{ marginTop: 12 }}>
              <div className="ai-card-header">
                <div>
                  <p className="ai-card-note">Conversation Summary</p>
                  <h3 className="ai-card-title">AI Summary</h3>
                </div>
              </div>
              <div style={{ color: "#0f172a", lineHeight: 1.6 }}>
                {eventDetails.summary ||
                  "Conversation received. Review transcript for details."}
              </div>
            </div>

            <div className="ai-card" style={{ marginTop: 12 }}>
              <div className="ai-card-header">
                <div>
                  <p className="ai-card-note">Recommended Next Step</p>
                  <h3 className="ai-card-title">Follow-Up</h3>
                </div>
              </div>
              <div style={{ color: "#0f172a", lineHeight: 1.6 }}>
                {eventDetails.nextStep || "No action needed"}
              </div>
            </div>

            <div className="ai-card" style={{ marginTop: 12 }}>
              <div className="ai-card-header">
                <div>
                  <p className="ai-card-note">Transcript</p>
                  <h3 className="ai-card-title">Conversation Messages</h3>
                </div>
              </div>
              {transcriptHealth?.incomplete && transcriptHealth.banner ? (
                <div
                  role="status"
                  style={{
                    marginBottom: 12,
                    padding: "10px 12px",
                    borderRadius: 10,
                    background: "#fff7ed",
                    border: "1px solid #fed7aa",
                    color: "#9a3412",
                    lineHeight: 1.5,
                    fontSize: 14,
                  }}
                >
                  {transcriptHealth.banner}
                </div>
              ) : null}
              {eventDetails.transcriptRows.length ? (
                <div style={{ display: "grid", gap: 8 }}>
                  {eventDetails.transcriptRows.map((row, idx) => (
                    <div
                      key={`${idx}`}
                      style={getTranscriptBubbleStyle(row?.role)}
                    >
                      <div style={{ fontWeight: 600, color: "#334155", marginBottom: 4 }}>
                        {row?.role || "Caller"}
                      </div>
                      <span style={{ color: "#0f172a" }}>{row?.message || "--"}</span>
                    </div>
                  ))}
                </div>
              ) : eventDetails.transcriptText ? (
                <pre
                  style={{
                    margin: 0,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    color: "#0f172a",
                  }}
                >
                  {eventDetails.transcriptText}
                </pre>
              ) : (
                <div style={{ color: "#64748b" }}>--</div>
              )}
            </div>

          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Agent247Settings;
